import { Modal, Typography } from "antd"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { documentTypeIconLookup } from "../helpers/document-type-visuals.ts"
import { renderDocumentTypeDecoration } from "../helpers/render-metadata-decoration.tsx"
import type { DocumentTypeOption } from "../hooks/useDocumentTypeOptions.ts"
import { MetadataSelect } from "./MetadataSelect.tsx"
import { type FileItem } from "../types/file-item.types.ts"

const { Text } = Typography

interface AssignDocumentTypeModalProps {
  file: FileItem
  options: DocumentTypeOption[]
  loadFailed: boolean
  onClose: () => void
  onSubmit: (value: string | null) => Promise<boolean>
}

/** A file's Document Type is assigned/changed/cleared here — a single
 *  lightweight modal reached from the row menu, rather than a persistent
 *  always-visible column: most files in All Files are unclassified, and a
 *  column that reads "—" for most rows is exactly what this replaces. */
export function AssignDocumentTypeModal({
  file,
  options,
  loadFailed,
  onClose,
  onSubmit
}: AssignDocumentTypeModalProps) {
  const { t } = useTranslation()
  const documentTypeIcons = useMemo(() => documentTypeIconLookup(options), [options])

  return (
    <Modal
      title={t("files.documentType.assignTitle")}
      open
      onCancel={onClose}
      footer={null}
    >
      <Text
        type="secondary"
        className="block mb-3"
        ellipsis={{ tooltip: file.name }}
      >
        {file.name}
      </Text>
      <MetadataSelect
        autoFocus
        open
        value={file.documentType}
        options={options}
        renderDecoration={(value) => renderDocumentTypeDecoration(value, documentTypeIcons[value])}
        loadFailedText={loadFailed ? t("files.documentType.loadFailed") : undefined}
        placeholder={t("files.documentType.assignType")}
        className="w-full"
        onChange={async (value) => {
          const ok = await onSubmit(value)
          if (ok) onClose()
          return ok
        }}
      />
    </Modal>
  )
}
